import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Package, Truck, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function ShopSuccess() {
  const { shopOrderStatus, photoBookDownloadUrl, raffleTicketsUrl } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await shopOrderStatus(sessionId);
        if (cancelled) return;
        setOrder(res.order);
        if (res.book_downloads) setDownloads(res.book_downloads);
        if (res.raffle_tickets) setTickets(res.raffle_tickets);
        if (res.payment_status === 'paid') { setState('paid'); return; }
        tries += 1;
        if (tries >= 6) { setState('pending'); return; }
        setTimeout(poll, 1500);
      } catch (e) {
        if (!cancelled) setState(e.response?.status === 403 ? 'forbidden' : 'error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, shopOrderStatus]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 fade-in" data-testid="shop-success-page">
      <div className="bg-white rounded-lg border shadow-sm p-8 text-center" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        {state === 'checking' && (
          <div data-testid="shop-success-checking">
            <Loader2 size={44} className="mx-auto mb-3 animate-spin" style={{ color: 'var(--navy)' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Confirming your order…</h1>
            <p className="text-sm" style={{ color: 'var(--olive)' }}>Please wait while we confirm your payment.</p>
          </div>
        )}
        {state === 'paid' && (
          <div data-testid="shop-success-paid">
            <CheckCircle2 size={52} className="mx-auto mb-3" style={{ color: 'var(--navy)' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Thank you for your order!</h1>
            <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>Your payment was successful and a receipt has been emailed to you.</p>
            {tickets && (
              <div className="rounded-lg border p-3 mb-4 text-left" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(122,86,44,0.04)' }} data-testid="shop-success-tickets">
                <div className="font-title text-sm mb-1" style={{ color: 'var(--navy)' }}>Your raffle tickets — {tickets.title}</div>
                <p className="text-sm mb-2">Ticket number{tickets.numbers.length > 1 ? 's' : ''}: <b data-testid="shop-success-ticket-numbers">{tickets.numbers.map((n) => String(n).padStart(4, '0')).join(', ')}</b></p>
                <a href={raffleTicketsUrl(tickets.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded font-title text-sm" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="shop-success-tickets-download"><Download size={14} /> Download tickets (PDF)</a>
                <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>The same link is in your emailed receipt. Good luck in the draw!</p>
              </div>
            )}
            {downloads.length > 0 && (
              <div className="rounded-lg border p-3 mb-4 text-left" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'rgba(122,86,44,0.04)' }} data-testid="shop-success-downloads">
                <div className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Your photo book downloads</div>
                {downloads.map((d) => (
                  <a key={d.book_id} href={photoBookDownloadUrl(d.book_id, d.token)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded font-title text-sm mr-2 mb-1" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid={`shop-success-download-${d.book_id}`}><Download size={14} /> {d.title} (PDF)</a>
                ))}
                <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>We've also emailed you this download link.</p>
              </div>
            )}
          </div>
        )}
        {state === 'pending' && (
          <div data-testid="shop-success-pending">
            <Loader2 size={44} className="mx-auto mb-3" style={{ color: 'var(--olive)' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Payment processing</h1>
            <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>We haven't confirmed your payment yet. If you completed checkout, it will update shortly.</p>
          </div>
        )}
        {state === 'forbidden' && (
          <div data-testid="shop-success-forbidden">
            <CheckCircle2 size={52} className="mx-auto mb-3" style={{ color: 'var(--navy)' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Thank you — payment received</h1>
            <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>This order belongs to a member account. Sign in to see the details — your receipt and any raffle tickets have also been emailed to you.</p>
            <Link to={`/login?next=${encodeURIComponent(`/shop/success?session_id=${sessionId}`)}`} className="inline-block px-5 py-2.5 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="shop-success-signin">Sign in to view order</Link>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="shop-success-error">
            <XCircle size={52} className="mx-auto mb-3" style={{ color: '#8a1f11' }} />
            <h1 className="font-title text-2xl mb-1" style={{ color: 'var(--maroon)' }}>Order not found</h1>
            <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>We couldn't find this order. If you were charged, please contact us — your receipt and any raffle tickets are also emailed to you.</p>
          </div>
        )}

        {order && (
          <div className="text-left mt-4 border-t pt-4" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid="shop-success-summary">
            <h3 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>Order summary</h3>
            {(order.items || []).map((it, i) => (
              <div key={i} className="flex justify-between text-sm mb-1">
                <span>{it.name} × {it.qty}</span>
                <span>{money(it.line_total)}</span>
              </div>
            ))}
            {order.postage_total > 0 && (
              <div className="flex justify-between text-sm mb-1"><span>Postage & handling</span><span>{money(order.postage_total)}</span></div>
            )}
            <div className="flex justify-between font-title text-sm mt-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
              <span>Total</span><span>{money(order.total)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm mt-3" style={{ color: 'var(--olive)' }}>
              {order.fulfillment === 'delivery' ? <Truck size={16} /> : <Package size={16} />}
              {order.fulfillment === 'delivery' ? 'Delivery to your address' : 'Pickup'}
            </div>
          </div>
        )}

        <Link to="/shop" data-testid="shop-back-link" className="inline-block mt-6 px-4 py-2 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
          Back to Shop
        </Link>
      </div>
    </div>
  );
}
